import { useState, useEffect, useCallback, useRef } from 'react';
import {
  connectWallet,
  getAccounts,
  getChainId,
  getNonce as getNonceUtil,
  signMessage as signMessageUtil,
  signTypedData as signTypedDataUtil,
  verifyTypedDataSignature as verifyTypedDataSignatureUtil,
  onAccountsChanged,
  onChainChanged,
  isProviderAvailable,
  chainIdToDecimal,
  EIP712TypedData,
  createEIP712TypedData,
  getTypedDataV4Hash,
} from '../utils/web3Provider';
import useAlerting from './useAlerting';

interface UseWeb3Options {
  allowedSignerAddresses?: string[];
}

export interface UseWeb3Return {
  // State
  account: string | null;
  chainId: string | null;
  chainIdDecimal: number | null;
  isConnected: boolean;
  isProviderAvailable: boolean;
  error: string | null;
  isLoading: boolean;

  // Actions
  connect: () => Promise<void>;
  ensureWalletConnected: () => Promise<void>;
  getNonce: (block?: string) => Promise<number>;
  signMessage: (message: string) => Promise<string>;
  signTypedData: (typedData: EIP712TypedData) => Promise<string>;
  signTypedDataWithWrappedPayload: (typedData: EIP712TypedData) => Promise<string>;
  verifyTypedDataSignature: (
    typedData: EIP712TypedData,
    signature: string,
    expectedAddress?: string
  ) => boolean;
  disconnect: () => void;
}

export function useWeb3(options: UseWeb3Options = {}): UseWeb3Return {
  const [account, setAccount] = useState<string | null>(null);
  const [chainId, setChainId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { alerting } = useAlerting();

  const allowedSignerAddressesRef = useRef<string[] | undefined>(options.allowedSignerAddresses);

  const providerAvailable = isProviderAvailable();
  const isConnected = account !== null;
  const chainIdDecimal = chainId ? chainIdToDecimal(chainId) : null;

  const pickAllowedAccount = (accounts: string[]): string | null => {
    if (!accounts || accounts.length === 0) {
      return null;
    }

    const allowed = allowedSignerAddressesRef.current;
    if (!allowed || allowed.length === 0) {
      return accounts[0] ?? null;
    }

    const filtered = accounts.filter((acc) =>
      allowed.map((a) => a.toLowerCase()).includes(acc.toLowerCase())
    );

    console.log('[pickAllowedAccount] ', {
      metamaskAccounts: accounts,
      allowedSignerAddresses: allowed,
      connected: filtered,
      selected: filtered[0] ?? null,
    });

    return filtered[0] ?? null;
  };

  useEffect(() => {
    allowedSignerAddressesRef.current = options.allowedSignerAddresses;
  }, [options.allowedSignerAddresses]);

  useEffect(() => {
    const syncConnection = async () => {
      if (!providerAvailable) {
        setAccount(null);
        setChainId(null);
        return;
      }

      try {
        const accounts = await getAccounts();
        const selected = pickAllowedAccount(accounts);

        setAccount(selected);

        if (!selected) {
          setChainId(null);
          return;
        }

        const currentChainId = await getChainId();
        setChainId(currentChainId);
      } catch (err) {
        console.error('Failed to sync wallet connection:', err);
      }
    };

    syncConnection();
  }, [providerAvailable, options.allowedSignerAddresses]);

  // Listen to account changes
  useEffect(() => {
    if (!providerAvailable) {
      return;
    }

    const cleanup = onAccountsChanged((accounts) => {
      console.log('Accounts changed:', accounts);
      if (accounts.length > 0) {
        const selected = pickAllowedAccount(accounts);
        setAccount(selected);
      } else {
        setAccount(null);
      }
    });

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [providerAvailable]);

  // Listen to chain changes
  useEffect(() => {
    if (!providerAvailable) {
      return;
    }

    const cleanup = onChainChanged((newChainId) => {
      console.log('Chain changed:', newChainId);
      setChainId(newChainId);
    });

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [providerAvailable]);

  const handleError = useCallback(
    ({ errorMsg, isAlert = true }: { errorMsg: string; isAlert?: boolean }) => {
      console.error(errorMsg);
      setError(errorMsg);
      if (isAlert) {
        alerting('error', errorMsg);
      }
    },
    [alerting]
  );

  // Connect wallet
  const connect = useCallback(async () => {
    if (!providerAvailable) {
      handleError({ errorMsg: 'No Ethereum provider found.' });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const accounts = await connectWallet();

      if (!accounts || accounts.length === 0) {
        handleError({ errorMsg: 'No available accounts found.' });
        return;
      }

      const selectedAccount = pickAllowedAccount(accounts);

      if (!selectedAccount) {
        const allowed = allowedSignerAddressesRef.current;

        if (allowed && allowed.length > 0) {
          handleError({
            errorMsg: 'Please select correct signer address in Web3 browser, e.g. Metamask.',
          });
          setAccount(null);
          return;
        }

        handleError({ errorMsg: 'No available accounts found.' });
        return;
      }

      setAccount(selectedAccount);

      const currentChainId = await getChainId();
      if (currentChainId) {
        setChainId(currentChainId);
      }
    } catch (err) {
      handleError({ errorMsg: 'Failed to connect wallet.' });
    } finally {
      setIsLoading(false);
    }
  }, [providerAvailable, handleError]);

  // Sign message
  const signMessage = useCallback(
    async (message: string): Promise<string> => {
      if (!account) {
        const errorMsg = 'No account connected. Please connect wallet first.';
        console.error(errorMsg);
        setError(errorMsg);
        return '';
      }

      setIsLoading(true);
      setError(null);

      const signature = await signMessageUtil(message, account);

      if (!signature) {
        const errorMsg = 'Failed to sign message';
        setError(errorMsg);
      }

      setIsLoading(false);
      return signature;
    },
    [account]
  );

  // Sign typed data
  const signTypedData = useCallback(
    async (typedData: EIP712TypedData): Promise<string> => {
      if (!account) {
        const errorMsg = 'No account connected. Please connect wallet first.';
        console.error(errorMsg);
        setError(errorMsg);
        return '';
      }

      setIsLoading(true);
      setError(null);

      console.log('[Sign] Signing Typed Data: ', {
        typedData,
        account,
      });

      const signature = await signTypedDataUtil(account, typedData);

      console.log('[Sign] Payload: ', {
        account,
        completePayload: typedData,
      });

      if (!signature) {
        const errorMsg = 'Failed to sign typed data';
        setError(errorMsg);
        return '';
      }
      setIsLoading(false);

      console.log('[Sign] Signature: ', signature);

      return signature;
    },
    [account]
  );

  const verifyTypedDataSignature = useCallback(
    (typedData: EIP712TypedData, signature: string, expectedAddress?: string): boolean => {
      const signerToCompare = expectedAddress ?? account;

      if (!signerToCompare) {
        const errorMsg = 'No account connected. Please connect wallet first.';
        console.error(errorMsg);
        setError(errorMsg);
        return false;
      }

      if (!signature) {
        const errorMsg = 'Signature is required for verification.';
        console.error(errorMsg);
        setError(errorMsg);
        return false;
      }

      return verifyTypedDataSignatureUtil(signerToCompare, typedData, signature);
    },
    [account]
  );

  const signTypedDataWithWrappedPayload = useCallback(
    async (typedData: EIP712TypedData): Promise<string> => {
      const typedDataWrapped = createEIP712TypedData(typedData);
      const signature = await signTypedData(typedDataWrapped);

      console.log('[Sign] Typed Data Hash: ' + getTypedDataV4Hash(typedData));

      return signature;
    },
    [signTypedData]
  );

  // Get nonce for connected account
  const getNonce = useCallback(
    async (block: string = 'latest'): Promise<number> => {
      if (!account) {
        const errorMsg = 'No account connected. Please connect wallet first.';
        console.error(errorMsg);
        setError(errorMsg);
        return -1;
      }

      const nonce = await getNonceUtil(account, block);

      if (nonce === -1) {
        const errorMsg = 'Failed to get nonce';
        setError(errorMsg);
      }

      return nonce;
    },
    [account]
  );

  const ensureWalletConnected = async (): Promise<void> => {
    if (!isConnected || !account) {
      console.log('Connecting wallet..');
      await connect();
    }

    if (!account) {
      const errorMsg = error || 'Failed to connect wallet';
      throw new Error(errorMsg);
    }
  };

  // Disconnect (local state only)
  const disconnect = useCallback(() => {
    setAccount(null);
    setChainId(null);
    setError(null);
  }, []);

  return {
    account,
    chainId,
    chainIdDecimal,
    isConnected,
    isProviderAvailable: providerAvailable,
    error,
    isLoading,
    connect,
    ensureWalletConnected,
    getNonce,
    signMessage,
    signTypedData,
    signTypedDataWithWrappedPayload,
    verifyTypedDataSignature,
    disconnect,
  };
}
